import type { ScopeSectionRuleTypeName } from '@ydistri/api-sdk';
import React, { useCallback } from 'react';
import AddScopeSectionRule from './AddScopeSectionRule';
import type { ScopeRuleDefinition, ScopeSectionRuleDraft } from './scopeEditorModel';
import { createRule } from './scopeEditorModel';
import ScopeSectionRule from './ScopeSectionRule';
import type { ScopeRuleMultiselectEntity } from './scopeActiveEntityOptions';
import {
  RulesTable,
  RulesTableOverflow,
  SectionContainer,
} from './ScopeSectionRulesStyledComponents';

interface ScopeSectionRulesProps {
  getRuleMultiselectData: (
    type: ScopeSectionRuleTypeName,
    selectedValueIds?: number[],
  ) => ScopeRuleMultiselectEntity[];
  isLoading?: boolean;
  readOnly?: boolean;
  ruleDefinitions: ScopeRuleDefinition[];
  ruleMessagesById?: Record<string, string[]>;
  rules: ScopeSectionRuleDraft[];
  onRulesChange: (rules: ScopeSectionRuleDraft[]) => void;
}

const ScopeSectionRules: React.FC<ScopeSectionRulesProps> = ({
  getRuleMultiselectData,
  isLoading = false,
  readOnly = false,
  ruleDefinitions,
  ruleMessagesById = {},
  rules,
  onRulesChange,
}) => {
  const addRule = useCallback(
    (type: ScopeSectionRuleTypeName) => {
      onRulesChange([...rules, createRule(type)]);
    },
    [onRulesChange, rules],
  );
  const updateRule = useCallback(
    (updatedRule: ScopeSectionRuleDraft) => {
      onRulesChange(rules.map(rule => (rule.id === updatedRule.id ? updatedRule : rule)));
    },
    [onRulesChange, rules],
  );
  const removeRule = useCallback(
    (ruleId: string) => {
      onRulesChange(rules.filter(rule => rule.id !== ruleId));
    },
    [onRulesChange, rules],
  );

  return (
    <SectionContainer>
      <RulesTableOverflow>
        <RulesTable>
          <tbody>
            {rules.map(rule => (
              <ScopeSectionRule
                getRuleMultiselectData={getRuleMultiselectData}
                isLoading={isLoading}
                key={rule.id}
                label={
                  ruleDefinitions.find(definition => definition.value === rule.type)?.label ??
                  rule.type
                }
                readOnly={readOnly}
                rule={rule}
                warningMessages={ruleMessagesById[rule.id]}
                onChange={updateRule}
                onRemove={() => removeRule(rule.id)}
              />
            ))}
            <AddScopeSectionRule
              disabled={isLoading || readOnly}
              ruleDefinitions={ruleDefinitions}
              rules={rules}
              onAdd={addRule}
            />
          </tbody>
        </RulesTable>
      </RulesTableOverflow>
    </SectionContainer>
  );
};

export default ScopeSectionRules;
